
import { useEffect, useState } from "react";
import { getUserPurchases, Purchase } from "@/lib/purchases";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShoppingBag, Loader2, Calendar, CreditCard } from "lucide-react";
import { format } from "date-fns";

type PurchaseHistoryProps = {
  userId: string | undefined;
};

const PurchaseHistory = ({ userId }: PurchaseHistoryProps) => {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPurchases = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userPurchases = await getUserPurchases(userId);
        setPurchases(userPurchases);
      } catch (error) {
        console.error("Error fetching purchases:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchases();
  }, [userId]);

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd.MM.yyyy HH:mm");
  };

  const formatAmount = (amount: number) => {
    return (amount / 100).toFixed(2) + " €";
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-kaija-accent" />
      </div>
    );
  }

  if (purchases.length === 0) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Ostuajalugu</h2>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShoppingBag className="h-5 w-5 text-kaija-accent" />
              Ostuajalugu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <ShoppingBag className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium mb-2">
                Sul pole veel ühtegi ostu
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Sinu ostuajalugu kuvatakse siin pärast esimese ostu sooritamist.
                Vaata meie kursuseid ja leia endale sobiv!
              </p>
            </div>
          </CardContent>
        </Card>
        
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h3 className="text-sm font-medium text-blue-800 mb-1">Märkus</h3>
          <p className="text-sm text-blue-700">
            Kui sul on küsimusi oma varasema makse või ostu kohta, 
            siis palun võta minuga ühendust e-maili aadressil kaija@kaijaalliksaar.ee
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Ostuajalugu</h2>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShoppingBag className="h-5 w-5 text-kaija-accent" />
            Ostuajalugu ({purchases.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableCaption>Sinu ostuajalugu</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Kuupäev</TableHead>
                  <TableHead>Kursus</TableHead>
                  <TableHead>Summa</TableHead>
                  <TableHead>Staatus</TableHead>
                  <TableHead>Makseviis</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchases.map((purchase) => (
                  <TableRow key={purchase.id}>
                    <TableCell className="whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                        {formatDate(purchase.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {purchase.course?.title || "Kursus pole saadaval"}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatAmount(purchase.amount)}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                        purchase.status === 'completed' 
                          ? 'bg-green-100 text-green-800' 
                          : purchase.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {purchase.status === 'completed' 
                          ? 'Tehtud' 
                          : purchase.status === 'pending' 
                          ? 'Ootel' 
                          : 'Ebaõnnestunud'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <CreditCard className="h-4 w-4 mr-2 text-gray-500" />
                        {purchase.payment_method}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h3 className="text-sm font-medium text-blue-800 mb-1">Märkus</h3>
        <p className="text-sm text-blue-700">
          Kui sul on küsimusi oma ostu kohta, siis kirjuta mulle aadressil kaija@kaijaalliksaar.ee
        </p>
      </div>
    </div>
  );
};

export default PurchaseHistory;
